import R from 'ramda'
import { createIV } from '../core/random'
import { encryptObject, decryptObject } from '../core/aes'
import { createSymmetricEncryptionKeyFromPublicKey, createSymmetricEncryptionKeyFromPrivateKey } from '../core/ecc'
import { encrypt as encryptBlock } from './block'
import { createHeaderWrapperEncryptionParams, createEncryptionParams, createRecord } from './format'

/**
 * Encrypt record
 *
 * @name encryptRecord
 * @param {ElGamalPublicKey} headerKey public key for header encryption
 * @param {RecordMetadata} metadata
 * @param {{text: String, files: {name: String, size: Number, type: String, id: String, path: String, service: String, data: ArrayBuffer}[], passwords: {password: String, hint: String, iterations: Number}[], publicKeys: {id: String, version: String, key: ElGamalPublicKey}[]}} blocks
 * @return {Record} the encrypted record
 * @throws {Error} if a parameter is invalid
 * @public
 * @example
 *  const headerKey = CryptolizeCore.createAsymmetricKeys().public
 *
 *  const metadata = CryptolizeCore.createRecordMetadata(
 *    'id',
 *    'creator',
 *    'personalPage',
 *    'signature',
 *    'signatureKeyVersion',
 *    new Date().getTime(),
 *    new Date().getTimezoneOffset(),
 *    false,
 *    new Date().getTime(),
 *    'subject',
 *    'origin',
 *    {
 *      key1: 'value1',
 *      key2: 'value2'
 *    }
 *  )
 *
 *  const password = { password: 'password', hint: 'hint', iterations: 100000 }
 *  const publicKey = { id: 'publicKeyOwnerId', version: CryptolizeCore.createAsymmetricKeys().public, key: CryptolizeCore.createAsymmetricKeys().public }
 *  const text = 'text'
 *  const file = {
 *    name: 'name',
 *    size: 1024,
 *    type: 'type',
 *    id: 'id',
 *    path: 'path',
 *    service: 'service',
 *    data: 'ArrayBuffer'
 *  }
 *  const block = { passwords: [password], publicKeys: [publicKey], text: 'text', files: [file] }
 *
 *  const record = CryptolizeCore.encryptRecord(headerKey, metadata, [block])
 *  console.log(record)
 */
export function encrypt (headerKey, metadata, blocks) {
  const { key: headerEncryptionKey, tag } = createSymmetricEncryptionKeyFromPublicKey(headerKey)
  const headerEncryptionParams = createEncryptionParams(createIV())
  const headerWrapperEncryptionParams = createHeaderWrapperEncryptionParams(headerKey, tag, headerEncryptionParams)
  return R.compose(
    R.converge(
      R.curry(createRecord)(R.__, headerWrapperEncryptionParams, R.__),
      [
        R.compose(R.curry(encryptObject)(R.__, headerEncryptionKey, headerEncryptionParams), R.merge({ metadata }), R.objOf('blocks'), R.map(R.prop('header'))),
        R.map(R.dissoc('header'))
      ]
    ),
    R.map(R.apply(R.curry(encryptBlock)(R.__, R.__, R.__, R.__))),
    R.map(R.props(['passwords', 'publicKeys', 'text', 'files']))
  )(blocks)
}

/**
 * Decrypt record
 *
 * @name decryptRecord
 * @param {Record} record
 * @param {ElGamalPrivateKey} headerKey
 * @return {HeaderWrapper} header wrapper
 * @throws {Error} if a parameter is invalid, data is corrupt or wrong key
 * @public
 * @example
 *  const keys = CryptolizeCore.createAsymmetricKeys()
 *  const record = CryptolizeCore.encryptRecord(..., keys.public, ...)
 *  const headerWrapper = CryptolizeCore.decryptRecord(record, keys.private)
 *  console.log(headerWrapper)
 */
export function decrypt (record, headerKey) {
  return decryptObject(record.headerWrapper, createSymmetricEncryptionKeyFromPrivateKey(headerKey, record.headerWrapperEncryptionParams.tag), record.headerWrapperEncryptionParams.encryptionParams)
}



// WEBPACK FOOTER //
// ./src/record/record.js